import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, Anchor, Ship, Sailboat, Waves } from 'lucide-react';

interface Habilitacao {
  id: string;
  title: string;
  abbr: string;
  description: string;
  icon: React.ReactNode;
  requirements: { text: string; allowed: boolean }[];
  profile: { icon: React.ReactNode; title: string; description: string };
}

const habilitacoes: Habilitacao[] = [
  {
    id: 'arrais',
    title: 'Arrais Amador',
    abbr: 'ARA',
    description:
      'A porta de entrada para a náutica. Habilita o condutor a navegar em limites internos (rios, lagos, represas, baías e enseadas abrigadas).',
    icon: <Anchor className="w-5 h-5" />,
    requirements: [
      { text: 'Navegação Interior (Águas Abrigadas)', allowed: true },
      { text: 'Exige prova teórica na Marinha', allowed: true },
      { text: 'Exige 6h de aula prática embarcada', allowed: true },
      { text: 'Proibido navegar em mar aberto', allowed: false },
    ],
    profile: {
      icon: <Sailboat className="w-10 h-10 text-gold" />,
      title: 'Perfil do Aluno',
      description:
        'Iniciantes, pescadores esportivos, proprietários de lanchas de passeio em represas.',
    },
  },
  {
    id: 'mestre',
    title: 'Mestre Amador',
    abbr: 'MSA',
    description:
      'Para quem quer ir além. Permite navegar em águas costeiras, entre portos nacionais, até 20 milhas da costa.',
    icon: <Ship className="w-5 h-5" />,
    requirements: [
      { text: 'Navegação Costeira (até 20 milhas)', allowed: true },
      { text: 'Exige prova teórica avançada', allowed: true },
      { text: 'Requer ser Arrais Amador previamente', allowed: true },
      { text: 'Sem exigência de prática na escola', allowed: true },
    ],
    profile: {
      icon: <Ship className="w-10 h-10 text-gold" />,
      title: 'Perfil do Aluno',
      description:
        'Navegadores que desejam explorar a costa, viagens entre cidades e estados litorâneos.',
    },
  },
  {
    id: 'capitao',
    title: 'Capitão Amador',
    abbr: 'CPA',
    description:
      'A habilitação máxima para amadores. Permite navegação sem limites, incluindo travessias oceânicas internacionais.',
    icon: <Anchor className="w-5 h-5" />,
    requirements: [
      { text: 'Navegação sem limites (Oceânica)', allowed: true },
      { text: 'Prova teórica muito complexa (Astronômica)', allowed: true },
      { text: 'Requer ser Mestre Amador previamente', allowed: true },
      { text: 'Sem exigência de prática na escola', allowed: true },
    ],
    profile: {
      icon: <Anchor className="w-10 h-10 text-gold" />,
      title: 'Perfil do Aluno',
      description:
        'Navegadores experientes que planejam travessias oceânicas e costas internacionais.',
    },
  },
  {
    id: 'motonauta',
    title: 'Motonauta (Jet Ski)',
    abbr: 'MTA',
    description:
      'Categoria exclusiva para condução de Moto Aquática (Jet Ski). Frequentemente realizada em conjunto com o curso de Arrais.',
    icon: <Waves className="w-5 h-5" />,
    requirements: [
      { text: 'Exclusivo para Jet Ski', allowed: true },
      { text: 'Aulas práticas obrigatórias (3h)', allowed: true },
      { text: 'Prova teórica específica', allowed: true },
      { text: 'Pode ser averbada na carteira de Arrais/Mestre', allowed: true },
    ],
    profile: {
      icon: <Waves className="w-10 h-10 text-gold" />,
      title: 'Perfil do Aluno',
      description: 'Usuários de Jet Ski para lazer, verão e esportes aquáticos.',
    },
  },
];

const HabilitacoesSection: React.FC = () => {
  const [activeTab, setActiveTab] = useState('arrais');
  const active = habilitacoes.find((h) => h.id === activeTab)!;

  return (
    <section id="habilitacoes" className="py-24 sm:py-32 bg-primary relative overflow-hidden">

      {/* Grid overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)
          `,
          backgroundSize: '48px 48px',
        }}
      />

      {/* Fade-out grid at edges */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse 70% 60% at 50% 50%, transparent 40%, hsl(222 47% 11%) 100%)
          `,
        }}
      />

      {/* Gold atmospheric orb */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] pointer-events-none animate-glow-pulse"
        style={{
          background: 'radial-gradient(circle, hsl(43 74% 53% / 0.06) 0%, transparent 60%)',
          filter: 'blur(60px)',
        }}
      />

      <div className="container mx-auto px-6 relative z-10">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-gold/10 border border-gold/20 text-gold text-sm font-semibold tracking-wider uppercase mb-4">
            Educação Náutica
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-primary-foreground">
            Hierarquia das Habilitações
          </h2>
          <p className="mt-4 text-lg text-primary-foreground/55 max-w-3xl mx-auto">
            O sistema brasileiro de amadores é escalonado. Cada escola náutica
            prepara o aluno para um nível específico de competência e alcance
            geográfico.
          </p>
        </motion.div>

        {/* Tabs */}
        <div className="mb-12">
          <div className="flex flex-wrap justify-center gap-2 sm:gap-0 sm:border-b border-white/[0.1]">
            {habilitacoes.map((hab) => (
              <button
                key={hab.id}
                onClick={() => setActiveTab(hab.id)}
                className={`
                  relative px-6 py-3 text-sm sm:text-base font-semibold transition-all duration-300
                  ${activeTab === hab.id
                    ? 'text-gold'
                    : 'text-primary-foreground/40 hover:text-primary-foreground/80'
                  }
                `}
              >
                <span className="flex items-center gap-2">
                  {hab.icon}
                  <span className="hidden sm:inline">{hab.title}</span>
                  <span className="sm:hidden">{hab.abbr}</span>
                </span>
                {activeTab === hab.id && (
                  <motion.div
                    layoutId="habActiveTab"
                    className="absolute bottom-0 left-0 right-0 h-[3px] rounded-t-full overflow-hidden"
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  >
                    <div
                      className="absolute inset-0"
                      style={{
                        background: 'linear-gradient(90deg, hsl(var(--gold-dark)), hsl(var(--gold-light)), hsl(var(--gold-dark)))',
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 2s linear infinite',
                      }}
                    />
                    <div
                      className="absolute inset-0 blur-[3px]"
                      style={{ background: 'hsl(var(--gold) / 0.8)' }}
                    />
                  </motion.div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.35 }}
            className="max-w-5xl mx-auto"
          >
            {/* Gold animated border wrapper */}
            <div
              className="relative p-[1.5px] rounded-2xl"
              style={{
                background: 'linear-gradient(135deg, hsl(43 74% 53% / 0.6) 0%, hsl(222 47% 40% / 0.2) 40%, hsl(43 74% 63% / 0.5) 70%, hsl(43 74% 53% / 0.6) 100%)',
                backgroundSize: '300% 300%',
                animation: 'gradient-border-shift 5s ease infinite',
                boxShadow: '0 0 60px hsl(43 74% 53% / 0.1), 0 8px 40px -12px hsl(0 0% 0% / 0.4)',
              }}
            >
              <div
                className="rounded-2xl overflow-hidden"
                style={{ background: 'hsl(222 47% 14%)' }}
              >
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">

                  {/* Left: Details */}
                  <div className="lg:col-span-3 p-8 lg:p-10">
                    <h3 className="text-2xl font-heading font-bold text-primary-foreground mb-2">
                      {active.title}{' '}
                      <span className="text-primary-foreground/35 font-normal text-lg">
                        ({active.abbr})
                      </span>
                    </h3>
                    <p className="text-primary-foreground/60 mb-8 leading-relaxed">
                      {active.description}
                    </p>
                    <div className="space-y-4">
                      {active.requirements.map((req, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, x: -16 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{
                            delay: i * 0.09,
                            type: 'spring',
                            stiffness: 280,
                            damping: 24,
                          }}
                          className="flex items-center gap-3 group"
                        >
                          {req.allowed ? (
                            <div className="w-6 h-6 rounded-full bg-emerald-500/15 flex items-center justify-center flex-shrink-0 transition-all duration-200 group-hover:bg-emerald-500/25">
                              <Check className="w-4 h-4 text-emerald-400" />
                            </div>
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-red-500/15 flex items-center justify-center flex-shrink-0 transition-all duration-200 group-hover:bg-red-500/25">
                              <X className="w-4 h-4 text-red-400" />
                            </div>
                          )}
                          <span className="text-primary-foreground/75">{req.text}</span>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Right: Profile Card */}
                  <div
                    className="lg:col-span-2 border-t lg:border-t-0 lg:border-l p-8 lg:p-10 flex flex-col items-center justify-center text-center"
                    style={{ borderColor: 'rgba(255,255,255,0.07)', background: 'hsl(222 47% 12%)' }}
                  >
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: 0.2, type: 'spring', stiffness: 200, damping: 20 }}
                      className="w-20 h-20 rounded-2xl bg-gold/10 flex items-center justify-center mb-6 animate-float"
                      style={{
                        boxShadow: '0 0 25px hsl(var(--gold) / 0.22), 0 0 50px hsl(var(--gold) / 0.1)',
                      }}
                    >
                      {active.profile.icon}
                    </motion.div>
                    <h4 className="font-heading text-xl font-bold text-primary-foreground mb-3">
                      {active.profile.title}
                    </h4>
                    <p className="text-primary-foreground/55 text-sm leading-relaxed">
                      {active.profile.description}
                    </p>
                  </div>

                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

      </div>
    </section>
  );
};

export default HabilitacoesSection;
